import Vue from 'vue';
import { Notification } from 'element-ui';
import HTTP from '@/utils/http';
import { EventBus } from '@/utils/event-bus';
import i18n from '@/locales';
import partiesModule from '@/store/modules/envelopes/parties';
import shareableResource from '@/store/modules/mixins/shareableResource';
import { captureSentryMessage } from '@/lib/sentry';

// TODO consolidate the three envelopes into one

const defaultState = () => ({
  results: null,
  creating: {
    processedDocuments: 0,
    envelope: {
      company_id: null,
      documents: [],
      fingerprint: null,
      id: null,
      status: null,
      title: null,
      is_parallel_signing_enabled: null,
    },
  },
  viewing: {
    parties: [],
    envelope: {
      fields: [],
      documents: [],
      sharing: {
        groups: [],
        users: [],
      },
      everyone_permission: null,
    },
    log: {},
  },
  updating: {
    parties: [],
    envelope: {
      fields: [],
      documents: [],
    },
  },
});

/**
 * Helper functions for parallel signing feature
 */
const isParallelSigningEnabled = value => value === 'parallel';
const parallelSigningReadableValue = value => (value ? 'parallel' : 'sequential');

const envelopesModule = {
  namespaced: true,
  modules: {
    parties: partiesModule,
  },
  state: defaultState(),
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, defaultState());
    },
    SET_ENVELOPES(state, { results }) {
      state.results = results;
    },
    SET_VIEWING_ENVELOPE(state, { envelope }) {
      state.viewing.envelope = envelope;
    },
    SET_DOCUMENT_PROCESSED(state, { processed }) {
      const count = state.creating.processedDocuments;
      state.creating.processedDocuments = processed ? (count + 1) : count;
    },
    SET_UPDATING_ENVELOPE(state, { envelope }) {
      state.updating.envelope = envelope;
    },
    SET_VIEWING_AUDIT_LOG(state, { log }) {
      state.viewing.log = log;
    },
    SET_VIEWING_PARTIES(state, { parties }) {
      state.viewing.parties = parties;
    },
    SET_CREATING_ENVELOPE(state, { envelope }) {
      state.creating.envelope = envelope;
    },
    UPDATE_CREATING_ENVELOPE(state, { envelope }) {
      state.creating.envelope = { ...state.creating.envelope, ...envelope };
    },
    SET_CREATING_PARTIES(state, { parties }) {
      state.creating.envelope.parties = parties;
    },
    SET_UPDATING_PARTIES(state, { parties }) {
      state.updating.envelope.parties = parties;
    },
    DELETE_ENVELOPE_FIELD(state, { contractId, id }) {
      const doc = state.updating.envelope.documents.filter(item => item.contract_id === contractId)[0];
      const fields = doc.fields.filter(item => item.id !== id);
      Vue.set(doc, 'fields', fields);
    },
    SET_ENVELOPE_TITLE(state, { title }) {
      state.creating.envelope.title = title;
    },
    SET_SIGNING_SEQUENCE(state, { value }) {
        state.creating.envelope.is_parallel_signing_enabled = isParallelSigningEnabled(value);
    },
  },
  actions: {
    RESET_STATE({ commit }) {
      commit('RESET_STATE');
    },
    GET_ENVELOPES({ commit }) {
      return HTTP({
        url: 'envelopes',
      }).then((response) => {
        const results = response.data.data;
        commit('SET_ENVELOPES', { results });
      });
    },
    // TODO isCreating is a hack
    GET_ENVELOPE({ commit }, { fingerprint, isUpdate = false, isCreating = false }) {
      if (!fingerprint || fingerprint === 'undefined') { // Stop loading if it's not a real ID
        return Promise.reject(new Error("Fingerprint doesn't exist"));
      }
      return HTTP({
        url: `envelopes/${fingerprint}`,
      }).then((response) => {
        const envelope = response.data.data;
        if (isCreating) {
          commit('UPDATE_CREATING_ENVELOPE', { envelope });
        } else if (isUpdate) {
          commit('SET_UPDATING_ENVELOPE', { envelope });
        } else {
          commit('SET_VIEWING_ENVELOPE', { envelope });
        }
        return envelope;
      });
    },
    UPDATE_ENVELOPE(context, { fingerprint, data, suppressAllErrors = false }) {
      return HTTP({
        method: 'put',
        url: `envelopes/${fingerprint}`,
        data: {
          ...data,
        },
        suppressAllErrors,
      }).then((response) => {
        const envelope = response.data.data;
        return envelope;
      });
    },
    GET_ENVELOPE_AUDIT_LOG({ commit }, { fingerprint }) {
      if (!fingerprint || fingerprint === 'undefined') { // Stop loading if it's not a real ID
        return Promise.reject(new Error("Fingerprint doesn't exist"));
      }
      return HTTP({
        url: `envelopes/${fingerprint}/audit-log`,
      }).then((response) => {
        const log = response.data.data;
        commit('SET_VIEWING_AUDIT_LOG', { log });
        return log;
      });
    },
    CREATE({ commit }, { sharing, autoRemindHours }) {
      return HTTP({
        method: 'post',
        url: 'envelopes',
        data: {
          sharing,
          auto_remind_hours: autoRemindHours,
        },
      }).then((response) => {
        const envelope = response.data.data;
        commit('SET_CREATING_ENVELOPE', { envelope });
        return envelope;
      });
    },
    SEND({ commit, getters }, { title, isUpdate = false }) {
      const fingerprint = isUpdate ? getters.updating.fingerprint : getters.creating.fingerprint;
      return HTTP({
        method: 'put',
        url: `envelopes/${fingerprint}/send`,
        data: {
          title,
        },
      }).then((response) => {
        commit('UPDATE_CREATING_ENVELOPE', { envelope: response.data.data });
        return response.data.data;
      }).catch((error) => {
        Notification({
          type: 'error',
          title: i18n.t('message.fail.send', { name: i18n.t('functional.envelope') }),
          message: error.message,
          position: 'bottom-left',
        });
      });
    },
    async ADD_ENVELOPE_DOCUMENTS({ commit, getters }, { documents }) {
      let data;
      if (documents[0] instanceof File) {
        data = new FormData();
        [...documents].forEach(file =>
          data.append('documents[]', file));
      } else {
        data = {
          documents,
        };
      }

      const { fingerprint: envelopeFingerprint } = getters.creating;
      const { data: { data: envelope } } = await HTTP({
        data,
        method: 'post',
        url: `envelopes/${envelopeFingerprint}/documents`,
      });

      commit('UPDATE_CREATING_ENVELOPE', { envelope });
      return envelope;
    },
    ADD_PARTIES({ commit, getters }, {
      parties, fingerprint, isViewing = true, isCreating = false,
    }) {
      const envelopeFingerprint = fingerprint || getters.creating.fingerprint;

      parties.forEach((party) => {
        const match = party.email.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
        if (!match) {
          captureSentryMessage('Invalid party email detected', (scope) => {
            scope.setExtra('party', party);
          });
        }
      });

      return HTTP({
        method: 'post',
        url: `envelopes/${envelopeFingerprint}/parties`,
        data: {
          parties,
        },
      }).then((response) => {
        const envelope = response.data.data;
        if (isCreating) {
          commit('SET_CREATING_PARTIES', { parties: envelope.parties });
        } else if (isViewing) {
          commit('SET_VIEWING_PARTIES', { parties: envelope.parties });
        } else {
          commit('SET_UPDATING_PARTIES', { parties: envelope.parties });
        }
        return envelope;
      });
    },
    ADD_FIELDS({ getters, commit }, { fields, isUpdate = false }) {
      const fingerprint = isUpdate ? getters.updating.fingerprint : getters.creating.fingerprint;
      const documents = isUpdate ? getters.updating.documents : getters.creatingDocuments;
      const envelopeFields = [];
      fields.forEach((field) => {
        const contractId = documents[field.docIndex].contract_id;
        const defaultHeight = field.type === 'checkbox' ? 2 : 3;
        const defaultWidth = field.type === 'checkbox' ? 3 : 23;
        const partyId = field.party.id;
        const newField = {
          contract_id: contractId,
          party_id: partyId,
          page: field.page,
          type: field.type,
          group: field.group,
          choices: !field.choices.length && field.type === 'dropdown' ? [{ name: '--Default Option--' }] : field.choices,
          title: field.title || 'Untitled',
          required: field.required || false,
          top: field.top,
          left: field.left,
          x: field.x,
          y: field.y,
          height: field.height ? field.height : `${defaultHeight}%`,
          width: field.width ? field.width : `${defaultWidth}%`,
          merge_field: field.merge_field,
        };
        envelopeFields.push(newField);
      });

      return HTTP({
        method: 'post',
        url: `envelopes/${fingerprint}/fields`,
        data: {
          fields: envelopeFields,
        },
      }).then((response) => {
        const envelope = response.data.data;
        commit('SET_UPDATING_ENVELOPE', { envelope });
        return envelope;
      });
    },
    UPDATE_FIELDS({ commit, getters }, { fields, isUpdate = false, isCreating = false }) {
      const fingerprint = isUpdate ? getters.updating.fingerprint : getters.creating.fingerprint;
      const documents = isUpdate ? getters.updating.documents : getters.creatingDocuments;
      const envelopeFields = [];
      let missingIds;

      for (let i = 0; i < fields.length; i += 1) {
        const field = fields[i];
        const {
          width, height, left, top, required, title, page, id, group, choices, content,
        } = field;

        const contractId = field.contract_id || documents[field.docIndex].contract_id;
        const partyId = field.party_id;

        missingIds = partyId === 0 || id === 0;

        if (missingIds) break;

        // convert the font to uppercase
        const font = field?.font ? field.font.charAt(0).toUpperCase() + field.font.slice(1) : '';

        const updateField = {
          contract_id: contractId,
          party_id: partyId,
          id,
          title,
          required,
          top,
          left,
          height,
          width,
          font,
          page,
          group,
          choices: !choices.length && field.type === 'dropdown' ? [{ name: '--Default Option--' }] : choices,
          content,
        };

        envelopeFields.push(updateField);
      }

      if (missingIds) return false;

      return HTTP({
        method: 'put',
        url: `envelopes/${fingerprint}/fields`,
        data: {
          fields: envelopeFields,
        },
      }).then((response) => {
        const envelope = response.data.data;
        if (isCreating) {
          commit('UPDATE_CREATING_ENVELOPE', { envelope });
        } else if (isUpdate) {
          commit('SET_UPDATING_ENVELOPE', { envelope });
        } else {
          commit('SET_VIEWING_ENVELOPE', { envelope });
        }
        return envelope;
      });
    },
    DELETE_FIELDS({ commit, getters }, { fields, isUpdate = false }) {
      const fingerprint = isUpdate ? getters.updating.fingerprint : getters.creating.fingerprint;
      const parties = isUpdate ? getters.updating.parties : getters.viewingParties;
      const documents = isUpdate ? getters.updating.documents : getters.creatingDocuments;
      const envelopeFields = [];

      fields.forEach((field) => {
        const contractId = documents[field.docIndex].contract_id;
        const fieldNo = field.party.number ? field.party.number - 1 : field.party.order;
        const partyId = parties[fieldNo].id;
        const deleteField = {
          id: field.id,
          contract_id: contractId,
          party_id: partyId,
        };
        envelopeFields.push(deleteField);
        commit('DELETE_ENVELOPE_FIELD', { contractId, id: field.id });
      });

      return HTTP({
        method: 'delete',
        url: `envelopes/${fingerprint}/fields`,
        data: {
          fields: envelopeFields,
        },
      }).then((response) => {
        const envelope = response.data.data;
        if (isUpdate) {
          commit('SET_UPDATING_ENVELOPE', { envelope });
        } else {
          commit('SET_VIEWING_ENVELOPE', { envelope });
        }
        return envelope;
      });
    },
    DELETE_ENVELOPE_DOCUMENTS({ commit, getters }, { documentId }) {
      const { fingerprint } = getters.creating;

      return HTTP({
        method: 'delete',
        url: `envelopes/${fingerprint}/documents/${documentId}`,
      }).then((response) => {
        const envelope = response.data.data;
        commit('UPDATE_CREATING_ENVELOPE', { envelope });
        return envelope;
      });
    },
    UPDATE_PARTY({ commit }, party) {
      return HTTP({
        method: 'put',
        url: `envelopes/${party.fingerprint}/parties/${party.id}`,
        data: {
          email: party.email,
        },
      }).then((response) => {
        commit('SET_VIEWING_PARTIES', { parties: response.data.data });
        return response.data;
      });
    },
    SEND_ONE_OFF_REMINDER(context, { fingerprint, parties }) {
      return HTTP({
        method: 'put',
        url: `envelopes/${fingerprint}/remind`,
        data: {
          fingerprint,
          parties,
        },
      }).then((response) => {
        EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('envelope.reminderSetModal.messageReminderSentSuccess'), icon: 'check', type: 'success' }));
        return response.data;
      });
    },
    SEND_WIDGET_VERIFICATION_EMAIL(context, { fingerprint }) {
      return HTTP({
        method: 'put',
        url: `envelopes/${fingerprint}/resend-widget`,
      }).then((response) => {
        EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('envelope.widgetReminder.emailSentSuccess'), icon: 'check', type: 'success' }));
        return response.data;
      });
    },
    SET_ENVELOPE_REMINDERS({ commit }, { reminders, isCreating = false }) {
      const autoRemindHours = reminders.days * 24;

      return HTTP({
        method: 'put',
        url: `envelopes/${reminders.fingerprint}/set-reminders`,
        data: {
          auto_remind_hours: autoRemindHours,
        },
      }).then((response) => {
        if (isCreating) {
          commit('UPDATE_CREATING_ENVELOPE', { envelope: response.data.data });
        } else {
          commit('SET_VIEWING_ENVELOPE', { envelope: response.data.data });
        }

        return response.data;
      });
    },
    SET_ENVELOPE_EXPIRY({ commit }, { expiry, isCreating = false }) {
      return HTTP({
        method: 'put',
        url: `envelopes/${expiry.fingerprint}`,
        data: {
          next_expire: expiry.timestamp,
        },
      }).then((response) => {
        if (isCreating) {
          commit('UPDATE_CREATING_ENVELOPE', { envelope: response.data.data });
        } else {
          commit('SET_VIEWING_ENVELOPE', { envelope: response.data.data });
        }

        return response.data;
      });
    },
    CANCEL_ENVELOPE_REMINDERS({ commit }, { reminders, isCreating = false }) {
      return HTTP({
        method: 'put',
        url: `envelopes/${reminders.fingerprint}/cancel-reminders`,
      }).then((response) => {
        if (isCreating) {
          commit('UPDATE_CREATING_ENVELOPE', { envelope: response.data.data });
        } else {
          commit('SET_VIEWING_ENVELOPE', { envelope: response.data.data });
        }
        return response.data;
      });
    },
    CANCEL_ENVELOPE({ commit }, fingerprint) {
      return HTTP({
        method: 'put',
        url: `envelopes/${fingerprint}/cancel`,
      }).then((response) => {
        commit('SET_VIEWING_ENVELOPE', { envelope: response.data.data });
        EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('envelope.cancelEnvelopeSuccess'), icon: 'check', type: 'success' }));
        return response.data;
      });
    },
    DELETE_ENVELOPE({ commit }, fingerprint) {
      return HTTP({
        method: 'delete',
        url: `envelopes/${fingerprint}`,
      }).then((response) => {
        commit('SET_VIEWING_ENVELOPE', { envelope: response.data.data });
        EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('message.success.removed', { name: 'envelope' }), icon: 'check', type: 'success' }));
        return response.data;
      });
    },
    EXPIRE_ENVELOPE({ commit }, fingerprint) {
      return HTTP({
        method: 'put',
        url: `envelopes/${fingerprint}/expire`,
      }).then((response) => {
        commit('SET_VIEWING_ENVELOPE', { envelope: response.data.data });
        return response.data;
      });
    },
    async SET_SIGNING_SEQUENCE({ commit, dispatch, getters }, value) {
        const previousValue = getters.creating.is_parallel_signing_enabled;

        if (parallelSigningReadableValue(previousValue) === value) {
            return;
        }

        try {
            commit('SET_SIGNING_SEQUENCE', { value });
            await dispatch('UPDATE_ENVELOPE', {
                fingerprint: getters.creating.fingerprint,
                data: {
                    is_parallel_signing_enabled: isParallelSigningEnabled(value),
                },
                suppressAllErrors: true,
            });
        } catch (e) {
            commit('SET_SIGNING_SEQUENCE', { value: parallelSigningReadableValue(previousValue) });
            e.failedValue = value;
            throw e;
        }
    },
  },
  getters: {
    ...shareableResource.getters,
    creationStep: (state, getters) => {
      const { envelope } = state.creating;

      const hasMergeFieldsSet = Object.values(getters.mergeFieldTags)
        .filter(content => content)
        .length;

      if (envelope.documents.length === 0) {
        return 0;
      } else if (envelope.parties.length === 0) {
        return 1;
      } else if (!hasMergeFieldsSet) {
        return 2;
      }

      return 3;
    },
    viewing: state => state.viewing.envelope,
    current: state => state.creating.envelope,
    documents: (state, getters) => getters.current.documents,
    parties: (state, getters) => getters.current.parties,
    uploadedDocuments: (state, getters) => getters.current.documents.filter(doc => !doc.is_template),
    hasFields: (state, getters) => !!getters.documents.reduce((acc, document) => acc + document.fields.length, 0),
    templates: (state, getters) => getters.current.documents.filter(doc => doc.is_template),
    viewingLog: state => state.viewing.log,
    creating: state => state.creating.envelope,
    creatingParties: state => state.creating.envelope.parties,
    viewingParties: state => state.viewing.parties,
    creatingDocuments: state => state.creating.envelope.documents,
    updating: state => state.updating.envelope,
    processedDocuments: (state) => {
      const documents = state.creating.envelope.documents.filter(doc => doc.is_template === 0);
      const documentsHaveBeenProcessed = documents.filter(doc => doc.processed_at > 0 && doc.is_template === 0).length === documents.length;

      return documentsHaveBeenProcessed;
    },
    updatingFields: (state, getters) => {
      const envelope = getters.updating;
      const { parties } = envelope;
      const fields = [];

      function getParty(id, array) {
        return array.filter(item => item.id === id)[0];
      }

      envelope.documents.forEach((doc) => {
        fields.push(...doc.fields.filter((field) => {
          Vue.set(field, 'party', getParty(field.party_id, parties));
          return field;
        }));
      });

      return fields;
    },
    mergeFieldTags: (state, getters) => {
      const allTags = getters.creatingDocuments
        // Get a list of all fields in the envelope
        .flatMap(document => document.fields)
        // Remove fields which are not merge fields
        .filter(field => field.merge_field)
        // Map to an array of objects with the merge field tag and the content
        .map(field => ({
          name: field.merge_field,
          content: field.content,
        }));

      // Convert the array to an object with the merge field tag as the key and the content as the value
      // By storing this in an object this also removes duplicate uses of the merge field tags
      return Object.assign({}, ...allTags.map(tag => ({ [tag.name]: tag.content })));
    },
    mergeFields: (state, getters) => {
      const fields = [];
      getters.creatingDocuments.forEach((doc) => {
        fields.push(...doc.fields.filter(field => field.merge_field !== ''));
      });
      return fields;
    },
    mergeFieldsForParty: (state, getters) => (id) => {
      const partyId = parseInt(id, 10);
      return getters.mergeFields.filter(field => parseInt(field.party_id, 10) === partyId);
    },
    fieldsForParty: (state, getters) => (id, viewing = false) => {
      const fields = [];
      const partyId = parseInt(id, 10);
      if (!viewing) {
        getters.creatingDocuments.forEach((doc) => {
          fields.push(...doc.fields.filter(field => field.merge_field === ''));
        });
      } else {
        getters.viewing.documents.forEach((doc) => {
          fields.push(...doc.fields);
        });
      }
      return fields.filter(field => parseInt(field.party_id, 10) === partyId);
    },
    currentSigningSequence: state => parallelSigningReadableValue(state.creating.envelope.is_parallel_signing_enabled),
  },
};

export default envelopesModule;
